import { MediaObject } from 'src/api/services/MediaClient';
import { ValueOfObject } from 'src/types/generics';
import { RevisionResponse } from 'src/lib/services/api/revision-api';
import { HasStatus, Submittable } from 'src/api/Interfaces';
import {
  MultipleOutcomesResponse,
  RequestAssetUrl,
  RequestComplexityType,
  RequestExtraObject,
  RequestPriorityType,
  RequestResolutionsObject,
  RequestStatus,
  requestStatus,
  TotalLengthResponse,
} from 'src/lib/services/api/request-api/request.types';
import { VideoTemplateResponse } from 'src/lib/services/api/video-templates-api';

export const requestOutcomeStatus = {
  ...requestStatus,
  archived: 'archived',
} as const;

export type RequestOutcomeStatus = ValueOfObject<typeof requestOutcomeStatus> | string;

export const requestListTab = {
  all: 'all',
  ...requestStatus,
  archived: 'archived',
} as const;

export type RequestListTab = ValueOfObject<typeof requestListTab>;

export const requestFlow = {
  default: 'default',
  multiOutcome: 'multi_outcome',
  outcome: 'outcome',
  repurpose: 'repurpose',
} as const;

export interface RequestsTableResponse extends HasStatus<RequestStatus>, Submittable {
  id: string;
  title: string;
  /** Parent request id — non-null only when the row is an outcome of a multi-outcome project. */
  parent_id: string | null;
  /** Parent request title — non-null only when the row is an outcome of a multi-outcome project. */
  parent_title: string | null;
  type: string;
  flow: 'default' | 'multi_outcome' | 'outcome' | string;
  priority: RequestPriorityType;
  editor_note?: string;
  assigned_editor?: {
    id: string;
    full_name: string;
    avatar?: MediaObject;
  };
  brand?: {
    id: string;
    name: string;
  };
  reviewed: string | null;
  resolutions?: RequestResolutionsObject;
  approved_at?: string;
  requested_reedit_at?: string;
  /** Lifetime count of customer-initiated re-edit requests on this row.
   * Ticks up on every Re-edit click; never cleared by Undo or by the editor
   * re-delivering, so it stays > 0 once any re-edit has happened until the
   * request reaches Complete/Archived. Drives the loader-trail signal that
   * survives an Undo (#2442). */
  reedit_count?: number;
  requested_attention_at?: string;
  attention_text?: string;
  created_at: Date | string;
  updated_at: Date | string;
  /** Stamped by the backend on activity triggers (status transitions to
   * editing/delivered/complete, attention/re-edit toggles, revision uploads,
   * chat messages, in-cycle editor reassignments). Drives the All-tab
   * default sort. */
  last_activity_at?: string | null;

  outcomes_count: number;
  complete_outcomes_count: number;
  delivered_outcomes_count: number;
  queued_outcomes_count: number;
  editing_outcomes_count: number;
  revisions_count?: number;
  rated_revisions_count?: number;

  repurposable_at?: string;
  repurposed_at?: string;
  due_date?: string | null;

  /** Only sent for approved rows — the only place the download action exists. */
  download?: RequestFilesDownload;
}

export const requestFilesDownloadState = {
  /** In hot storage, or unarchived and not yet expired. */
  available: 'available',
  /** In cold storage, nobody has asked for it back yet. */
  archived: 'archived',
  /** Unarchiving is running — S3 Glacier takes up to ~12h. */
  restoring: 'restoring',
  /** Was unarchived once, but the temporary copy has since expired. */
  expired: 'expired',
  /** Nothing was ever delivered on this request. */
  none: 'none',
} as const;

export type RequestFilesDownloadState = ValueOfObject<typeof requestFilesDownloadState> | string;

/**
 * State only, deliberately no file count: the row's count would come from the
 * database while the download resolves its files against storage, so the two
 * could disagree — and a number the table shows that the download then
 * contradicts is worse than no number at all.
 */
export type RequestFilesDownload = {
  state: RequestFilesDownloadState;
};

export type RequestFlow = ValueOfObject<typeof requestFlow> | string;

export type RequestOutcomeResponse = {
  id: string;
  status: RequestOutcomeStatus | string;
  title: string;
  type: string;
  total_length?: TotalLengthResponse;
  description: string;
  is_default: boolean;
  sort: number;
  editor_note?: string;

  revisions_count?: number;
  versions_count?: number;

  requested_reedit_at: string | null;
  /** Lifetime re-edit request counter. See RequestsTableResponse.reedit_count. */
  reedit_count?: number;
  requested_attention_at?: string | null;
  attention_text?: string;
  created_at: string;
  updated_at: string;
  due_date?: string | null;

  created_by: string;
  creator_id: string;
  company_id: string;
  parent_id: string;
  editor: {
    id: string;
    first_name: string;
    last_name: string;
    avatar?: MediaObject;
  };
  latest_revision?: {
    id: string;
    created_at: string;
    video?: MediaObject;
  };

  video_template?: {
    id: string;
    title: string;
  };
  aspect_ratio?: string;
  resize: string[];
};

export type RequestResponse = {
  id: string;
  title: string;
  type: string;
  status: RequestStatus;
  editor_note: string;
  attention_text?: string;
  flow: RequestFlow;
  priority: RequestPriorityType;
  complexity?: RequestComplexityType;
  parent_id: string | null;
  /** Parent project title — non-null only when this request is an outcome of a multi-outcome project. */
  parent_title?: string | null;

  reviewed: boolean;
  with_multiple_outcomes: boolean;

  resolutions?: RequestResolutionsObject;
  description?: {
    /** @deprecated */
    total_length?: string;
    video_request_description?: string;
  };
  total_length?: TotalLengthResponse;
  supporting_materials?: {
    assets_urls?: string[];
    music_preference?: string;
  };
  extra?: RequestExtraObject;

  reviewed_by_customer?: string;
  reviewed_by_editor?: string;
  status_changed_at?: string;
  submitted_at?: string;
  approved_at?: string;
  requested_reedit_at?: string;
  /** Lifetime re-edit request counter. See RequestsTableResponse.reedit_count. */
  reedit_count?: number;
  requested_attention_at?: string;
  created_at: string;

  revisions_count?: number;
  rated_revisions_count?: number;

  assets?: RequestAssetUrl[];
  editor?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    avatar?: MediaObject;
  };
  brand?: {
    id: string;
    name: string;
    description: string;
    colors?: { color: string }[];
    created_at: string;
  };
  company?: {
    id: string;
    name: string;
  };
  media?: {
    footage: MediaObject[];
  };

  revisions: RevisionResponse[];

  assigned_editor?: {
    id: string;
    first_name: string;
    last_name: string;
  };

  video_template?: VideoTemplateResponse;

  multiple_outcomes: MultipleOutcomesResponse | null;

  repurposable_at?: string;
  repurposed_at?: string;
  transcript_attached?: string;
  due_date?: string | null;
};

export type UpdatePriorityRequest = {
  priority: RequestPriorityType;
};

export interface ChangeStatusRequest {
  status: RequestStatus;
}

export type RequestRevisionPreviewResponse = {
  id: string;
  company_id: string;
  title: string;
  status: RequestStatus;
  type: string;
  main_revision_video?: MediaObject;
  repurposable_at?: string;
  due_date?: string | null;
  created_at: string;
  updated_at: string;
};

export type RepurposeRequestFormObject = {
  title: string;
  priority: RequestPriorityType;
  brand_id?: string;
  description?: string;
  examples?: string;
};
